import React, { useState, useEffect } from 'react';
import { Utensils, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../services/api';

/**
 * NutritionBaselineFormCard — chat onboarding form for nutrition targets.
 *
 * Same fields as the Settings/Profile page surface so there's ONE source of
 * truth for nutrition stats — PATCHes /api/nutrition/profile which writes to
 * user.profile.* (existing TDEE / macro math reads from there).
 *
 * On save: fires `wellness:refresh` so Dashboard rings + Nikki context pack
 * pick up the new targets immediately.
 */

const ACTIVITY = [
  { id: 'sedentary', label: 'Sedentary', desc: 'desk job, little/no exercise' },
  { id: 'light',     label: 'Light',     desc: 'light exercise 1–3 days/week' },
  { id: 'moderate',  label: 'Moderate',  desc: 'moderate 3–5 days/week' },
  { id: 'very',      label: 'Very Active', desc: 'hard exercise 6–7 days/week' },
  { id: 'extreme',   label: 'Extreme',   desc: 'pro athlete, physical job + training' },
];

const GOALS = [
  { id: 'lose',     label: 'Lose weight', desc: 'modest deficit, higher protein' },
  { id: 'maintain', label: 'Maintain',    desc: 'balanced — stay where you are' },
  { id: 'gain',     label: 'Gain (muscle)', desc: 'small surplus, build lean mass' },
];

const SEX = [
  { id: 'female', label: 'Female' },
  { id: 'male',   label: 'Male' },
  { id: 'other',  label: 'Other' },
];

const Section = ({ label, children }) => (
  <div className="space-y-2">
    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-secondary font-bold">{label}</p>
    {children}
  </div>
);

const Pill = ({ active, onClick, children, testId, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`px-3 py-1.5 rounded-sm text-xs font-secondary uppercase tracking-wider border transition-colors ${
      active
        ? 'bg-primary text-black border-primary'
        : 'bg-transparent text-zinc-300 border-zinc-700 hover:border-zinc-500'
    }`}
    data-testid={testId}
  >
    {children}
  </button>
);

const NutritionBaselineFormCard = ({ data }) => {
  const prefill = data?.prefill || {};
  const [heightCm, setHeightCm]   = useState(prefill.height_cm ?? '');
  const [weightKg, setWeightKg]   = useState(prefill.weight_kg ?? '');
  const [age, setAge]             = useState(prefill.age ?? '');
  const [sex, setSex]             = useState(prefill.sex ?? null);
  const [activity, setActivity]   = useState(prefill.activity_level ?? null);
  const [goal, setGoal]           = useState(prefill.goal ?? null);
  const [meals, setMeals]         = useState(prefill.meal_frequency ?? 3);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [targets, setTargets]     = useState(null);

  // Required for Nikki to compute targets — anything else is optional/improves accuracy.
  const canSave = weightKg && Number(weightKg) > 0 && goal;

  const handleSave = async () => {
    if (!canSave) {
      toast.error('Add at least weight + a goal so I can compute your targets.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        weight_kg: Number(weightKg),
        goal,
        meal_frequency: Number(meals) || 3,
      };
      if (heightCm) payload.height_cm = Number(heightCm);
      if (age) payload.age = Number(age);
      if (sex) payload.sex = sex;
      if (activity) payload.activity_level = activity;
      const res = await api.patchNutritionProfile(payload);
      setTargets(res.data?.targets || null);
      setSaved(true);
      toast.success('Targets locked in — Nikki has them now.');
      window.dispatchEvent(new Event('wellness:refresh'));
    } catch (e) {
      toast.error('Could not save — try again?');
    } finally {
      setSaving(false);
    }
  };

  // Saved confirmation view — show the computed targets so the user understands what just happened.
  if (saved && targets) {
    return (
      <div className="rounded-sm border border-primary/40 bg-zinc-900/60 p-4 space-y-3" data-testid="nutrition-baseline-saved">
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-primary" />
          <p className="text-[10px] uppercase tracking-[0.2em] text-primary font-secondary font-bold">YOUR DAILY TARGETS</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="border-l-2 border-primary pl-3"><p className="text-[10px] uppercase text-zinc-500">CALORIES</p><p className="font-secondary font-bold text-xl text-primary">{targets.daily_kcal_target}</p><p className="text-[10px] text-zinc-500">kcal/day</p></div>
          <div className="border-l-2 border-accent-purple pl-3"><p className="text-[10px] uppercase text-zinc-500">PROTEIN</p><p className="font-secondary font-bold text-xl text-accent-purple">{targets.protein_target_g}g</p><p className="text-[10px] text-zinc-500">/day</p></div>
          <div className="border-l-2 border-zinc-600 pl-3"><p className="text-[10px] uppercase text-zinc-500">CARBS · FAT</p><p className="font-secondary font-bold text-sm text-zinc-300">{targets.carb_target_g}g · {targets.fat_target_g}g</p></div>
        </div>
        <p className="text-[11px] text-zinc-500 leading-relaxed">Edit any time from Settings or just message me with updates.</p>
      </div>
    );
  }

  return (
    <div className="rounded-sm border border-zinc-700 bg-zinc-900/40 p-4 space-y-4" data-testid="nutrition-baseline-form">
      <div className="flex items-center gap-2">
        <Utensils className="w-4 h-4 text-primary" />
        <p className="text-[10px] uppercase tracking-[0.2em] text-primary font-secondary font-bold">NUTRITION BASELINE</p>
      </div>

      {/* Body stats */}
      <Section label="STATS">
        <div className="grid grid-cols-3 gap-2">
          <input type="number" inputMode="decimal" placeholder="Height cm"
            value={heightCm} onChange={e => setHeightCm(e.target.value)}
            className="hpme-input text-sm" data-testid="baseline-height-input" />
          <input type="number" inputMode="decimal" placeholder="Weight kg *"
            value={weightKg} onChange={e => setWeightKg(e.target.value)}
            className="hpme-input text-sm" data-testid="baseline-weight-input" />
          <input type="number" inputMode="numeric" placeholder="Age"
            value={age} onChange={e => setAge(e.target.value)}
            className="hpme-input text-sm" data-testid="baseline-age-input" />
        </div>
      </Section>

      <Section label="SEX">
        <div className="flex gap-2 flex-wrap">
          {SEX.map(s => (
            <Pill key={s.id} active={sex === s.id} onClick={() => setSex(s.id)} testId={`baseline-sex-${s.id}`}>{s.label}</Pill>
          ))}
        </div>
      </Section>

      <Section label="ACTIVITY LEVEL">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {ACTIVITY.map(a => (
            <Pill key={a.id} active={activity === a.id} onClick={() => setActivity(a.id)} testId={`baseline-activity-${a.id}`} title={a.desc}>{a.label}</Pill>
          ))}
        </div>
      </Section>

      <Section label="GOAL *">
        <div className="grid grid-cols-3 gap-2">
          {GOALS.map(g => (
            <Pill key={g.id} active={goal === g.id} onClick={() => setGoal(g.id)} testId={`baseline-goal-${g.id}`} title={g.desc}>{g.label}</Pill>
          ))}
        </div>
      </Section>

      <Section label="MEALS PER DAY">
        <div className="flex gap-2">
          {[2, 3, 4, 5, 6].map(n => (
            <Pill key={n} active={meals === n} onClick={() => setMeals(n)} testId={`baseline-meals-${n}`}>{n}</Pill>
          ))}
        </div>
      </Section>

      <button
        type="button"
        onClick={handleSave}
        disabled={!canSave || saving}
        className="w-full bg-primary text-black font-secondary font-bold uppercase tracking-wider py-2.5 rounded-sm hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="baseline-save-btn"
      >
        {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : 'Compute my targets'}
      </button>
      {!canSave && <p className="text-[10px] text-zinc-500 text-center">Weight + goal are needed minimum (others improve accuracy)</p>}
    </div>
  );
};

export default NutritionBaselineFormCard;
